import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FiSend, FiRefreshCw, FiPaperclip, FiMic, FiSearch, FiClock, FiFileText, FiSettings, FiList, FiX, FiChevronDown, FiChevronRight, FiMessageSquare, FiZap, FiLock, FiEdit3, FiCheck, FiFolder, FiSquare } from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import { streamChat, interruptChat, resumeWithAction, getAgent, getSessions, deleteSession, renameSession, getSessionMessages, getToolApprovals, sseErrorMessage, SESSION_TERMINATED_CODE, downloadHermesArtifact, type AgentDetail, type SseEvent, type ChatSessionRecord, type ChatMessageRecord, type ToolApprovalMode, } from './claudeAgentApi';
import { useAuth } from '../auth/AuthContext';
import { useInsuredDetail } from '../../context/InsuredDetailContext';
import { usePolicyDetail } from '../../context/PolicyDetailContext';
import { useInsuredMention, buildEntityXmlTag } from '../chat/useInsuredMention';
import InsuredMentionDropdown from '../chat/InsuredMentionDropdown';
import { uploadFile, uploadAmsFiles } from '../../services/fileUploadService';
import SelectFromAmsDialog, { AmsUploadSelection } from '../momentumcommand/SelectFromAmsDialog';
import { useTheme } from '../../context/ThemeContext';
import { parseApiDate } from '../../utils/dateTime';
import '../../styles/ChatInput.css';
import ProceedQuickReplies from '../../components/ui/ProceedQuickReplies';
import { asksToProceed } from '../../utils/proceedPrompt';
import CarrierHandoffFrame, { HANDOFF_LINK } from '../chat/CarrierHandoffFrame';
interface PendingAttachment {
    name: string;
    url: string;
}
const IcoEye: React.FC<{
    size?: number;
}> = ({ size = 12 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>);
const IcoExternalLink: React.FC<{
    size?: number;
}> = ({ size = 12 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
    <polyline points="15 3 21 3 21 9"/>
    <line x1="10" y1="14" x2="21" y2="3"/>
  </svg>);
function getGreeting(): string {
    const h = new Date().getHours();
    if (h < 12)
        return 'Good morning';
    if (h < 17)
        return 'Good afternoon';
    return 'Good evening';
}
function getTodayLabel(): string {
    return new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}
interface Message {
    id: string;
    role: 'user' | 'assistant' | 'tool';
    content: string;
    streaming?: boolean;
    isThinking?: boolean;
    toolName?: string;
    toolInput?: Record<string, unknown>;
    toolStatus?: 'running' | 'pending' | 'approved' | 'denied' | 'done';
    toolEventId?: string;
    toolSessionThreadId?: string | null;
    isError?: boolean;
    /** The user pressed Stop; the body holds whatever streamed before. */
    isStopped?: boolean;
    /** The AI session ended on the service side (SESSION_TERMINATED); the
     *  bubble offers "Start new chat". */
    isTerminated?: boolean;
    calledAt?: number;
    completedAt?: number;
    mcpServer?: string;
    fileAttachments?: PendingAttachment[];
    // AI-112 Part 2: files a Hermes- or Claude-backed agent created during this
    // turn (artifact.created SSE events), attached to whichever assistant
    // message was in flight when the event arrived. Rendered as click-to-
    // download chips — see downloadHermesArtifact in claudeAgentApi.ts.
    artifacts?: {
        name: string;
        artifactId: string;
    }[];
}
interface ToolLogEntry {
    id: string;
    name: string;
    input: Record<string, unknown>;
    status: 'running' | 'pending' | 'approved' | 'denied' | 'done';
    calledAt: number;
    completedAt?: number;
    mcpServer?: string;
}
const ToolLogPanel: React.FC<{
    entries: ToolLogEntry[];
    onClose: () => void;
}> = ({ entries, onClose }) => {
    const [expanded, setExpanded] = React.useState<string | null>(null);
    const fmt = (ts: number) => new Date(ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    const latency = (e: ToolLogEntry) => e.completedAt ? `${((e.completedAt - e.calledAt) / 1000).toFixed(2)}s` : '…';
    const statusColor = (s: string) => s === 'done' || s === 'approved' ? '#4ade80'
        : s === 'denied' ? '#f87171'
            : s === 'running' ? '#fb923c'
                : '#a78bfa';
    return (<div className="cac-tool-log">
      <div className="cac-tool-log__header">
        <div className="cac-tool-log__title">
          <FiList size={13}/>
          Tool Call Log
          <span className="cac-tool-log__count">{entries.length}</span>
        </div>
        <button className="cac-tool-log__close" onClick={onClose}><FiX size={13}/></button>
      </div>

      {entries.length === 0
            ? <p className="cac-tool-log__empty">No tool calls yet in this session.</p>
            : <div className="cac-tool-log__list">
            {[...entries].reverse().map(e => (<div key={e.id} className="cac-tool-log__entry">
                <div className="cac-tool-log__entry-head" onClick={() => setExpanded(ex => ex === e.id ? null : e.id)}>
                  <span className="cac-tool-log__status-dot" style={{ background: statusColor(e.status) }}/>
                  <span className="cac-tool-log__entry-name">{e.name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                  <span className="cac-tool-log__entry-time">{fmt(e.calledAt)}</span>
                  <span className="cac-tool-log__entry-lat">{latency(e)}</span>
                  {Object.keys(e.input).length > 0
                        ? expanded === e.id ? <FiChevronDown size={10}/> : <FiChevronRight size={10}/>
                        : null}
                </div>
                {e.mcpServer && <div className="cac-tool-log__server">{e.mcpServer}</div>}
                {expanded === e.id && Object.keys(e.input).length > 0 && (<div className="cac-tool-log__params">
                    {Object.entries(e.input).map(([k, v]) => (<div key={k} className="cac-tool-log__param">
                        <span className="cac-tool-log__param-key">{k}</span>
                        <span className="cac-tool-log__param-val">
                          {typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v)}
                        </span>
                      </div>))}
                  </div>)}
              </div>))}
          </div>}
    </div>);
};
const ClaudeMark: React.FC<{
    size?: number;
}> = ({ size = 22 }) => (<svg width={size} height={size} viewBox="0 0 48 48" fill="none" style={{ flexShrink: 0 }}>
    <defs>
      <linearGradient id="cm-g2" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#fb923c"/>
        <stop offset="100%" stopColor="#c2410c"/>
      </linearGradient>
    </defs>
    <rect width="48" height="48" rx="14" fill="url(#cm-g2)"/>
    <g transform="translate(24,24)">
      {([0, 45, 90, 135, 180, 225, 270, 315] as const).map(a => (<rect key={a} x="-2.6" y="-17" width="5.2" height="12" rx="2.6" fill="rgba(255,255,255,0.95)" transform={`rotate(${a})`}/>))}
    </g>
  </svg>);
function inlineFmt(line: string, key: string | number): React.ReactNode {
    const re = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`|\$\$(IDF)_([^#$]+)##([^$]+)\$\$|\$\$(PDF)_([^#$]+)##([^$]+)\$\$)/g;
    const nodes: React.ReactNode[] = [];
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(line)) !== null) {
        if (m.index > last)
            nodes.push(line.slice(last, m.index));
        if (m[2])
            nodes.push(<strong key={`${key}-b${m.index}`}>{m[2]}</strong>);
        else if (m[3])
            nodes.push(<em key={`${key}-i${m.index}`}>{m[3]}</em>);
        else if (m[4])
            nodes.push(<code key={`${key}-c${m.index}`} className="cac-inline-code">{m[4]}</code>);
        else if (m[5] === 'IDF') {
            const id = m[7].trim();
            nodes.push(<button key={`${key}-idf${m.index}`} className="cac-entity-link cac-entity-link--insured" onClick={() => _handleEntityClick?.('insured', id)}>
          <IcoEntityInsured />{m[6].trim()}
        </button>);
        }
        else if (m[8] === 'PDF') {
            const id = m[10].trim();
            nodes.push(<button key={`${key}-pdf${m.index}`} className="cac-entity-link cac-entity-link--policy" onClick={() => _handleEntityClick?.('policy', id)}>
          <IcoEntityPolicy />{m[9].trim()}
        </button>);
        }
        last = m.index + m[0].length;
    }
    if (last < line.length)
        nodes.push(line.slice(last));
    return nodes.length === 0 ? '' : nodes.length === 1 ? nodes[0] : <>{nodes}</>;
}
function isTableRow(s: string) { return s.trim().startsWith('|') && s.trim().endsWith('|'); }
function isSeparator(s: string) { return /^\|[\s|:-]+\|$/.test(s.trim()); }
function parseRow(s: string): string[] {
    return s.trim().slice(1, -1).split('|').map(c => c.trim());
}
export function renderMd(text: string): React.ReactNode {
    const lines = text.split('\n');
    const out: React.ReactNode[] = [];
    let i = 0;
    while (i < lines.length) {
        const raw = lines[i];
        const trimmed = raw.trim();
        if (!trimmed) {
            out.push(<div key={`g${i}`} className="cac-gap"/>);
            i++;
            continue;
        }
        if (/^---+$/.test(trimmed)) {
            out.push(<hr key={i} className="cac-hr"/>);
            i++;
            continue;
        }
        const hm = trimmed.match(/^(#{1,3})\s+(.+)$/);
        if (hm) {
            const lvl = hm[1].length as 1 | 2 | 3;
            out.push(<p key={i} className={`cac-heading cac-heading--${lvl}`}>{inlineFmt(hm[2], i)}</p>);
            i++;
            continue;
        }
        if (isTableRow(trimmed) && i + 1 < lines.length && isSeparator(lines[i + 1])) {
            const headers = parseRow(trimmed);
            i += 2;
            const bodyRows: string[][] = [];
            while (i < lines.length && isTableRow(lines[i])) {
                bodyRows.push(parseRow(lines[i]));
                i++;
            }
            out.push(<div key={`tbl${i}`} className="cac-table-wrap">
          <table className="cac-table">
            <thead>
              <tr>{headers.map((h, ci) => <th key={ci}>{inlineFmt(h, `th${i}-${ci}`)}</th>)}</tr>
            </thead>
            <tbody>
              {bodyRows.map((row, ri) => (<tr key={ri}>
                  {row.map((cell, ci) => <td key={ci}>{inlineFmt(cell, `td${i}-${ri}-${ci}`)}</td>)}
                </tr>))}
            </tbody>
          </table>
        </div>);
            continue;
        }
        if (/^\d+\.\s/.test(trimmed)) {
            const items: React.ReactNode[] = [];
            while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
                const content = lines[i].trim().replace(/^\d+\.\s+/, '');
                items.push(<li key={i}>{inlineFmt(content, i)}</li>);
                i++;
            }
            out.push(<ol key={`ol${i}`} className="cac-ol">{items}</ol>);
            continue;
        }
        if (/^[-*]\s/.test(trimmed)) {
            const items: React.ReactNode[] = [];
            while (i < lines.length && /^[-*]\s/.test(lines[i].trim())) {
                const content = lines[i].trim().replace(/^[-*]\s+/, '');
                items.push(<li key={i}>{inlineFmt(content, i)}</li>);
                i++;
            }
            out.push(<ul key={`ul${i}`} className="cac-ul">{items}</ul>);
            continue;
        }
        // A carrier hand-off link on its own line (a code to type, a choice to make): show the waiting question here as a card.
        if (HANDOFF_LINK.test(trimmed)) {
            out.push(<CarrierHandoffFrame key={`hf${i}`} url={trimmed}/>);
            i++;
            continue;
        }
        out.push(<span key={i} className="cac-line">{inlineFmt(trimmed, i)}</span>);
        i++;
    }
    return out;
}
const ENTITY_STRIP_RE = /\$\$(?:IDF|PDF)_[\s\S]*?(?:\$\$|$)/g;
function stripEntityTokens(text: string): string {
    return text.replace(ENTITY_STRIP_RE, '');
}
const ENTITY_TOKEN_RE = /\$\$(IDF)_([^#$]+)##([^$]+)\$\$|\$\$(PDF)_([^#$]+)##([^$]+)\$\$/g;
const USER_XML_ENTITY_RE = /<(insured|prospect|carrier|policy)>([\s\S]*?)<\/\1>/g;
function parseXmlEntity(tag: string): {
    type: string;
    name: string;
    id: string;
} | null {
    const typeM = /^<(insured|prospect|carrier|policy)>/.exec(tag);
    if (!typeM)
        return null;
    const nameM = /<name>([\s\S]*?)<\/name>/.exec(tag);
    const numM = /<number>([\s\S]*?)<\/number>/.exec(tag);
    const idM = /<id>([\s\S]*?)<\/id>/.exec(tag);
    return {
        type: typeM[1],
        name: (nameM?.[1] || numM?.[1] || '').trim(),
        id: (idM?.[1] || '').trim(),
    };
}
function extractAttachedFilesFromText(text: string): PendingAttachment[] {
    const rows: PendingAttachment[] = [];
    const lines = (text || '').split('\n');
    let inAttachedSection = false;
    for (const raw of lines) {
        const line = raw.trim();
        if (!line) {
            if (inAttachedSection)
                break;
            continue;
        }
        if (/^\[?Attached files\]?\s*:?$/i.test(line)) {
            inAttachedSection = true;
            continue;
        }
        if (line.startsWith('📎 ')) {
            rows.push({ name: line.slice(3).trim(), url: '' });
            continue;
        }
        if (!inAttachedSection)
            continue;
        const bullet = line.replace(/^[-*]\s*/, '').trim();
        const withUrl = bullet.match(/^(.+?)\s*:\s*(https?:\/\/\S.*)$/i);
        if (withUrl) {
            rows.push({ name: withUrl[1].trim(), url: withUrl[2].trim() });
            continue;
        }
        const urlOnly = bullet.match(/^(https?:\/\/\S.*)$/i);
        if (!urlOnly)
            continue;
        const url = urlOnly[1].trim();
        const rawName = url.split('?')[0].split('/').pop() || 'Attached file';
        rows.push({ name: decodeURIComponent(rawName), url });
    }
    return rows;
}
const IcoEntityInsured: React.FC = () => (<svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <circle cx="8" cy="5" r="3"/>
    <path d="M2 14c0-3.314 2.686-6 6-6s6 2.686 6 6"/>
  </svg>);
const IcoEntityPolicy: React.FC = () => (<svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M4 2h8a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V3a1 1 0 011-1z"/>
    <path d="M6 6h4M6 9h4M6 12h2"/>
  </svg>);
let _handleEntityClick: ((type: 'insured' | 'policy', id: string) => void) | null = null;
const InsuredChip: React.FC<{
    id: string;
    fallback: string;
    onClick: () => void;
}> = ({ id, fallback, onClick }) => {
    const [name, setName] = React.useState(fallback);
    React.useEffect(() => {
        const authToken = localStorage.getItem('authToken') || '';
        const amsToken = localStorage.getItem('amsToken') || '';
        fetch(`${(process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/ampnowcerts/insuredandprospectListById?insuredDatabaseId=${encodeURIComponent(id)}&nowCertsToken=${encodeURIComponent(amsToken)}`, { headers: { Authorization: `Bearer ${authToken}` } })
            .then(r => r.ok ? r.json() : null)
            .then(json => {
            if (!json)
                return;
            const item = Array.isArray(json) ? json[0] : json;
            if (!item)
                return;
            const resolved = item.commercialName || `${item.firstName || ''} ${item.lastName || ''}`.trim();
            if (resolved)
                setName(resolved);
        })
            .catch(() => { });
    }, [id]);
    return (<button className="cac-entity-link cac-entity-link--insured" onClick={onClick}>
      <IcoEntityInsured />{name}
    </button>);
};
const ThinkingIndicator: React.FC = () => (<div className="cac-thinking">
    <div className="cac-thinking__dots">
      {[0, 1, 2].map(i => <span key={i} className="cac-thinking__dot" style={{ animationDelay: `${i * 0.15}s` }}/>)}
    </div>
    <span className="cac-thinking__text">Thinking</span>
  </div>);
const TypingDots: React.FC = () => (<div className="cac-skeleton">
    <div className="cac-skeleton__line cac-skeleton__line--xl"/>
    <div className="cac-skeleton__line cac-skeleton__line--lg"/>
    <div className="cac-skeleton__line cac-skeleton__line--md"/>
  </div>);
function formatToolName(raw: string): string {
    return raw.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
type ToolStatus = 'running' | 'pending' | 'approved' | 'denied' | 'done';
const ThinkingBlock: React.FC<{
    items: Message[];
    isLive: boolean;
}> = ({ items, isLive }) => {
    const [open, setOpen] = React.useState(true);
    const [expandedTool, setExpandedTool] = React.useState<string | null>(null);
    React.useEffect(() => {
        if (isLive) {
            setOpen(true);
        }
        else {
            setExpandedTool(null);
            const t = setTimeout(() => setOpen(false), 900);
            return () => clearTimeout(t);
        }
    }, [isLive]);
    const toolMsgs = items.filter(m => m.role === 'tool');
    const textMsgs = items.filter(m => m.role === 'assistant' && m.content);
    const headerText = React.useMemo(() => {
        if (isLive)
            return 'Thinking…';
        if (toolMsgs.length > 0) {
            const names = Array.from(new Set(toolMsgs.map(t => formatToolName(t.toolName ?? 'tool'))));
            if (names.length === 1)
                return `Used ${names[0]}`;
            if (names.length === 2)
                return `Used ${names[0]} and ${names[1]}`;
            return `Used ${names[0]} and ${names.length - 1} more tools`;
        }
        if (textMsgs.length > 0) {
            const first = textMsgs[0].content.trim().replace(/\n+/g, ' ');
            return first.length > 90 ? first.slice(0, 87) + '…' : first;
        }
        return 'Processing…';
    }, [isLive, toolMsgs.length, textMsgs.length]);
    const totalTime = React.useMemo(() => {
        if (isLive || toolMsgs.length === 0)
            return null;
        const done = toolMsgs.filter(m => m.calledAt && m.completedAt);
        if (done.length === 0)
            return null;
        const total = done.reduce((acc, m) => acc + (m.completedAt! - m.calledAt!), 0) / 1000;
        return total.toFixed(1) + 's';
    }, [isLive, toolMsgs.length]);
    const latency = (m: Message) => m.completedAt && m.calledAt ? `${((m.completedAt - m.calledAt) / 1000).toFixed(1)}s` : null;
    return (<div className={`cac-thought${isLive ? ' cac-thought--live' : ' cac-thought--done'}`}>

      
      <button className="cac-thought__hdr" onClick={() => setOpen(o => !o)} type="button">
        <span className="cac-thought__hdr-left">
          {isLive ? (<span className="cac-thought__sparkle-wrap">
              <RiSparklingFill size={14} className="cac-thought__sparkle"/>
            </span>) : (<svg className="cac-thought__check" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle cx="7" cy="7" r="6" stroke="rgba(74,222,128,0.5)" strokeWidth="1.2"/>
              <path d="M4.2 7l2.1 2.1 3.5-3.5" stroke="#4ade80" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>)}
          <span className="cac-thought__label">{headerText}</span>
        </span>
        <span className="cac-thought__hdr-right">
          {!isLive && totalTime && (<span className="cac-thought__total-time">{totalTime}</span>)}
          {!isLive && toolMsgs.length > 0 && (<span className="cac-thought__tool-count">
              {toolMsgs.length} tool{toolMsgs.length > 1 ? 's' : ''}
            </span>)}
          <svg className={`cac-thought__chevron${open ? ' cac-thought__chevron--open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </button>

      
      {open && (<div className="cac-thought__body">
          {items.map(m => {
                if (m.role === 'tool') {
                    const lat = latency(m);
                    const isRunning = m.toolStatus === 'running';
                    const isDone = m.toolStatus === 'done' || m.toolStatus === 'approved';
                    const isDenied = m.toolStatus === 'denied';
                    const hasParams = m.toolInput && Object.keys(m.toolInput).length > 0;
                    const isExpanded = isLive ? isRunning : expandedTool === m.id;
                    return (<div key={m.id} className={`cac-thought__tool${isRunning ? ' cac-thought__tool--running' : isDone ? ' cac-thought__tool--done' : isDenied ? ' cac-thought__tool--denied' : ''}`}>
                  <div className={`cac-thought__tool-row${hasParams ? ' cac-thought__tool-row--expandable' : ''}`} onClick={() => !isLive && hasParams && setExpandedTool(isExpanded ? null : m.id)}>
                    
                    {isRunning ? (<span className="cac-thought__sparkle-wrap cac-thought__sparkle-wrap--sm">
                        <RiSparklingFill size={11} className="cac-thought__sparkle"/>
                      </span>) : isDone ? (<svg width="13" height="13" viewBox="0 0 13 13" fill="none" className="cac-thought__tool-status-icon">
                        <circle cx="6.5" cy="6.5" r="5.5" stroke="rgba(74,222,128,0.4)" strokeWidth="1"/>
                        <path d="M3.8 6.5l1.9 1.9 3.5-3.5" stroke="#4ade80" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>) : isDenied ? (<svg width="13" height="13" viewBox="0 0 13 13" fill="none" className="cac-thought__tool-status-icon">
                        <circle cx="6.5" cy="6.5" r="5.5" stroke="rgba(248,113,113,0.4)" strokeWidth="1"/>
                        <path d="M4.3 4.3l4.4 4.4M8.7 4.3l-4.4 4.4" stroke="#f87171" strokeWidth="1.4" strokeLinecap="round"/>
                      </svg>) : (<span className="cac-thought__item-dot" style={{ background: '#a78bfa' }}/>)}

                    
                    <span className="cac-thought__tool-name">{formatToolName(m.toolName ?? '')}</span>

                    
                    {m.mcpServer && <span className="cac-thought__tool-server">{m.mcpServer}</span>}

                    
                    <span className="cac-thought__tool-right">
                      {lat && <span className="cac-thought__tool-lat">{lat}</span>}
                      {hasParams && (<svg className={`cac-thought__tool-chevron${isExpanded ? ' cac-thought__tool-chevron--open' : ''}`} width="10" height="10" viewBox="0 0 10 10" fill="none">
                          <path d="M2.5 3.5l2.5 2.5 2.5-2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>)}
                    </span>
                  </div>

                  
                  {isExpanded && hasParams && (<div className="cac-thought__tool-params">
                      <div className="cac-thought__tool-params-label">Input</div>
                      <pre className="cac-thought__tool-json">
                        {JSON.stringify(m.toolInput, null, 2)}
                      </pre>
                    </div>)}
                </div>);
                }
                if (m.role === 'assistant') {
                    if (!m.content)
                        return null;
                    const preview = m.content.trim().replace(/\n+/g, ' ');
                    const short = preview.length > 220 ? preview.slice(0, 217) + '…' : preview;
                    return (<div key={m.id} className="cac-thought__item cac-thought__item--text">
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, opacity: 0.3 }}>
                    <path d="M2 3h8M2 6h6M2 9h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                  </svg>
                  <span className="cac-thought__item-preview">{short}</span>
                </div>);
                }
                return null;
            })}

          
          {!isLive && (<div className="cac-thought__done-row">
              <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                <circle cx="6" cy="6" r="5" stroke="rgba(74,222,128,0.3)" strokeWidth="1"/>
                <path d="M3.5 6l1.8 1.8 3-3" stroke="#4ade80" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="cac-thought__done-label">Done thinking</span>
            </div>)}
        </div>)}
    </div>);
};
const ApprovalPanel: React.FC<{
    tools: Message[];
    onApprove: (id: string, threadId?: string | null) => void;
    onDeny: (id: string, threadId?: string | null) => void;
}> = ({ tools, onApprove, onDeny }) => {
    const [openIdx, setOpenIdx] = React.useState(0);
    if (tools.length === 0)
        return null;
    const tool = tools[Math.min(openIdx, tools.length - 1)];
    const params = Object.entries(tool.toolInput ?? {});
    const paramStr = params.map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`).join(' · ');
    return (<div className="cac-approval-panel">
      <div className="cac-approval-panel__inner">
        
        <div className="cac-approval-panel__tool">
          <div className="cac-approval-panel__tool-icon">
            <svg width="11" height="11" viewBox="0 0 20 20" fill="none"><path d="M15 3a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h10zm-1 3H6v2h8V6zm0 4H6v2h8v-2z" fill="currentColor"/></svg>
          </div>
          <div className="cac-approval-panel__tool-meta">
            <span className="cac-approval-panel__tool-name">{formatToolName(tool.toolName ?? '')}</span>
            {paramStr && <span className="cac-approval-panel__tool-params">{paramStr}</span>}
          </div>
        </div>

        
        {tools.length > 1 && (<div className="cac-approval-panel__pager">
            {tools.map((_, i) => (<button key={i} className={`cac-approval-panel__dot${i === openIdx ? ' cac-approval-panel__dot--on' : ''}`} onClick={() => setOpenIdx(i)}/>))}
          </div>)}

        
        <div className="cac-approval-panel__actions">
          <button className="cac-approval-panel__btn cac-approval-panel__btn--deny" onClick={() => onDeny(tool.toolEventId!, tool.toolSessionThreadId)}>
            Deny
          </button>
          <button className="cac-approval-panel__btn cac-approval-panel__btn--allow" onClick={() => onApprove(tool.toolEventId!, tool.toolSessionThreadId)}>
            Allow
          </button>
        </div>
      </div>
    </div>);
};
const SUGGESTIONS: {
    Icon: React.FC<{
        size?: number;
    }>;
    text: string;
}[] = [
    { Icon: FiSearch, text: 'Look up insured by name or policy number' },
    { Icon: FiClock, text: 'Show me upcoming policy renewals' },
    { Icon: FiFileText, text: 'Find all active claims for an insured' },
    { Icon: FiSettings, text: 'What tools do you have available?' },
];
const WelcomeRecentSessions: React.FC<{
    onResume: (sessionId: string, title: string) => void;
}> = ({ onResume }) => {
    const [sessions, setSessions] = React.useState<ChatSessionRecord[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [open, setOpen] = React.useState(false);
    const [deleting, setDeleting] = React.useState<string | null>(null);
    const [editing, setEditing] = React.useState<string | null>(null);
    const [editVal, setEditVal] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const editRef = React.useRef<HTMLInputElement>(null);
    React.useEffect(() => {
        getSessions().then(s => setSessions(s.slice(0, 8))).catch(() => { }).finally(() => setLoading(false));
    }, []);
    React.useEffect(() => {
        if (editing)
            setTimeout(() => editRef.current?.focus(), 30);
    }, [editing]);
    const fmtDate = (iso: string) => {
        const d = parseApiDate(iso);
        if (!d)
            return '—';
        const now = new Date();
        const diffH = (now.getTime() - d.getTime()) / 3600000;
        if (diffH < 1)
            return `${Math.max(1, Math.round(diffH * 60))}m ago`;
        if (diffH < 24)
            return `${Math.round(diffH)}h ago`;
        if (diffH < 168)
            return `${Math.round(diffH / 24)}d ago`;
        return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
    };
    const handleDelete = async (e: React.MouseEvent, sid: string) => {
        e.stopPropagation();
        setDeleting(sid);
        try {
            await deleteSession(sid);
            setSessions(prev => prev.filter(s => s.session_id !== sid));
        }
        catch { }
        finally {
            setDeleting(null);
        }
    };
    const startEdit = (e: React.MouseEvent, s: ChatSessionRecord) => {
        e.stopPropagation();
        setEditing(s.session_id);
        setEditVal(s.title || '');
    };
    const commitEdit = async (sid: string) => {
        const trimmed = editVal.trim();
        if (!trimmed) {
            setEditing(null);
            return;
        }
        setSaving(true);
        try {
            await renameSession(sid, trimmed);
            setSessions(prev => prev.map(s => s.session_id === sid ? { ...s, title: trimmed } : s));
        }
        catch { }
        finally {
            setSaving(false);
            setEditing(null);
        }
    };
    return (<motion.div className="cac-welcome-recent" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
      <button className="cac-welcome-recent__header cac-welcome-recent__header--btn" onClick={() => setOpen(v => !v)}>
        <FiMessageSquare size={12}/>
        <span>RECENT CONVERSATIONS</span>
        <FiChevronDown size={13} className={`cac-welcome-recent__chevron${open ? ' cac-welcome-recent__chevron--open' : ''}`}/>
      </button>
      {open && (<div className="cac-welcome-recent__list">
          {loading
                ? [1, 2, 3, 4].map(i => <div key={i} className="cac-welcome-recent__skel"/>)
                : sessions.length === 0
                    ? <div className="cac-welcome-recent__empty">
                  <FiMessageSquare size={20}/>
                  <span>No conversations yet</span>
                  <p>Your past sessions will appear here</p>
                </div>
                    : sessions.map(s => (<div key={s.session_id} className={`cac-welcome-recent__item${editing === s.session_id ? ' cac-welcome-recent__item--editing' : ''}`} onClick={() => { if (editing !== s.session_id)
                        onResume(s.session_id, s.title); }}>
                    <FiMessageSquare size={13} className="cac-welcome-recent__item-icon"/>
                    {editing === s.session_id
                            ? <input ref={editRef} className="cac-history__edit-input" value={editVal} onChange={e => setEditVal(e.target.value)} onClick={e => e.stopPropagation()} onKeyDown={e => { if (e.key === 'Enter')
                                commitEdit(s.session_id); if (e.key === 'Escape')
                                setEditing(null); }}/>
                            : <span className="cac-welcome-recent__item-title">{s.title || 'Untitled session'}</span>}
                    {editing !== s.session_id && <span className="cac-welcome-recent__item-date">{fmtDate(s.last_active_at)}</span>}
                    <div className="cac-history__item-actions">
                      {editing === s.session_id
                            ? <button className="cac-history__action-btn cac-history__action-btn--save" onClick={e => { e.stopPropagation(); commitEdit(s.session_id); }}>
                            {saving ? <FiRefreshCw size={10} className="cac-skill-card__spinner"/> : <FiCheck size={10}/>}
                          </button>
                            : <button className="cac-history__action-btn cac-history__action-btn--edit" onClick={e => startEdit(e, s)} title="Rename">
                            <FiEdit3 size={10}/>
                          </button>}
                      {editing !== s.session_id && (<button className="cac-history__action-btn cac-history__action-btn--delete" onClick={e => handleDelete(e, s.session_id)} title="Delete">
                          {deleting === s.session_id ? <FiRefreshCw size={10} className="cac-skill-card__spinner"/> : <FiX size={10}/>}
                        </button>)}
                    </div>
                  </div>))}
        </div>)}
    </motion.div>);
};
const SessionHistoryPanel: React.FC<{
    onResume: (sessionId: string, title: string) => void;
    onClose: () => void;
}> = ({ onResume, onClose }) => {
    const [sessions, setSessions] = React.useState<ChatSessionRecord[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [deleting, setDeleting] = React.useState<string | null>(null);
    const [search, setSearch] = React.useState('');
    const [editing, setEditing] = React.useState<string | null>(null);
    const [editVal, setEditVal] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const editInputRef = React.useRef<HTMLInputElement>(null);
    React.useEffect(() => {
        getSessions().then(setSessions).catch(() => { }).finally(() => setLoading(false));
    }, []);
    React.useEffect(() => {
        if (editing)
            setTimeout(() => editInputRef.current?.focus(), 30);
    }, [editing]);
    const handleDelete = async (e: React.MouseEvent, sid: string) => {
        e.stopPropagation();
        setDeleting(sid);
        try {
            await deleteSession(sid);
            setSessions(prev => prev.filter(s => s.session_id !== sid));
        }
        catch { }
        finally {
            setDeleting(null);
        }
    };
    const startEdit = (e: React.MouseEvent, s: ChatSessionRecord) => {
        e.stopPropagation();
        setEditing(s.session_id);
        setEditVal(s.title || '');
    };
    const commitEdit = async (sid: string) => {
        const trimmed = editVal.trim();
        if (!trimmed) {
            setEditing(null);
            return;
        }
        setSaving(true);
        try {
            await renameSession(sid, trimmed);
            setSessions(prev => prev.map(s => s.session_id === sid ? { ...s, title: trimmed } : s));
        }
        catch { }
        finally {
            setSaving(false);
            setEditing(null);
        }
    };
    const fmtDate = (iso: string) => {
        const d = parseApiDate(iso);
        if (!d)
            return '—';
        const now = new Date();
        const diffMs = now.getTime() - d.getTime();
        const diffH = diffMs / 3600000;
        if (diffH < 1)
            return `${Math.max(1, Math.round(diffMs / 60000))}m ago`;
        if (diffH < 24)
            return `${Math.round(diffH)}h ago`;
        if (diffH < 168)
            return `${Math.round(diffH / 24)}d ago`;
        return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
    };
    const filtered = search.trim()
        ? sessions.filter(s => (s.title || '').toLowerCase().includes(search.toLowerCase()))
        : sessions;
    return (<div className="cac-history">
      
      <div className="cac-history__header">
        <FiMessageSquare size={12}/>
        <span className="cac-history__title">RECENT CONVERSATIONS</span>
      </div>

      
      <div className="cac-history__search-wrap">
        <FiSearch size={12} className="cac-history__search-icon"/>
        <input className="cac-history__search" placeholder="Search conversations…" value={search} onChange={e => setSearch(e.target.value)} autoComplete="off"/>
        {search && (<button className="cac-history__search-clear" onClick={() => setSearch('')}>
            <FiX size={11}/>
          </button>)}
      </div>

      
      {loading
            ? <div className="cac-history__loading">
            {[85, 65, 75, 55, 70].map((_w, i) => (<div key={i} className="cac-history__skel"/>))}
          </div>
            : filtered.length === 0
                ? <p className="cac-history__empty">{search ? 'No matching conversations.' : 'No past sessions yet.'}</p>
                : <div className="cac-history__list">
              {filtered.map(s => (<div key={s.session_id} className={`cac-history__item${editing === s.session_id ? ' cac-history__item--editing' : ''}`} onClick={() => { if (editing !== s.session_id) {
                        onResume(s.session_id, s.title);
                        onClose();
                    } }}>
                  <FiMessageSquare size={13} className="cac-history__item-icon"/>

                  {editing === s.session_id
                            ? <input ref={editInputRef} className="cac-history__edit-input" value={editVal} onChange={e => setEditVal(e.target.value)} onClick={e => e.stopPropagation()} onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        commitEdit(s.session_id);
                                    }
                                    if (e.key === 'Escape') {
                                        setEditing(null);
                                    }
                                }}/>
                            : <div className="cac-history__item-title">{s.title || 'Untitled session'}</div>}

                  {editing !== s.session_id && (<span className="cac-history__item-date">{fmtDate(s.last_active_at)}</span>)}

                  <div className="cac-history__item-actions">
                    {editing === s.session_id
                            ? <button className="cac-history__action-btn cac-history__action-btn--save" onClick={e => { e.stopPropagation(); commitEdit(s.session_id); }} title="Save">
                          {saving ? <FiRefreshCw size={10} className="cac-skill-card__spinner"/> : <FiCheck size={10}/>}
                        </button>
                            : <button className="cac-history__action-btn cac-history__action-btn--edit" onClick={e => startEdit(e, s)} title="Rename">
                          <FiEdit3 size={10}/>
                        </button>}
                    {editing !== s.session_id && (<button className="cac-history__action-btn cac-history__action-btn--delete" onClick={e => handleDelete(e, s.session_id)} title="Remove from history">
                        {deleting === s.session_id ? <FiRefreshCw size={10} className="cac-skill-card__spinner"/> : <FiX size={10}/>}
                      </button>)}
                  </div>
                </div>))}
            </div>}
    </div>);
};
function fileTypeInfo(name: string): {
    abbr: string;
    label: string;
    color: string;
    bg: string;
} {
    const ext = name.split('.').pop()?.toLowerCase() ?? '';
    const map: Record<string, {
        abbr: string;
        label: string;
        color: string;
        bg: string;
    }> = {
        pdf: { abbr: 'PDF', label: 'PDF Document', color: '#f87171', bg: 'rgba(248,113,113,0.15)' },
        jpg: { abbr: 'IMG', label: 'JPEG Image', color: '#34d399', bg: 'rgba(52,211,153,0.15)' },
        jpeg: { abbr: 'IMG', label: 'JPEG Image', color: '#34d399', bg: 'rgba(52,211,153,0.15)' },
        png: { abbr: 'PNG', label: 'PNG Image', color: '#34d399', bg: 'rgba(52,211,153,0.15)' },
        gif: { abbr: 'GIF', label: 'GIF Image', color: '#34d399', bg: 'rgba(52,211,153,0.15)' },
        webp: { abbr: 'IMG', label: 'WebP Image', color: '#34d399', bg: 'rgba(52,211,153,0.15)' },
        svg: { abbr: 'SVG', label: 'SVG Image', color: '#34d399', bg: 'rgba(52,211,153,0.15)' },
        docx: { abbr: 'DOC', label: 'Word Document', color: '#60a5fa', bg: 'rgba(96,165,250,0.15)' },
        doc: { abbr: 'DOC', label: 'Word Document', color: '#60a5fa', bg: 'rgba(96,165,250,0.15)' },
        xlsx: { abbr: 'XLS', label: 'Excel Sheet', color: '#4ade80', bg: 'rgba(74,222,128,0.15)' },
        xls: { abbr: 'XLS', label: 'Excel Sheet', color: '#4ade80', bg: 'rgba(74,222,128,0.15)' },
        csv: { abbr: 'CSV', label: 'CSV File', color: '#4ade80', bg: 'rgba(74,222,128,0.15)' },
        pptx: { abbr: 'PPT', label: 'PowerPoint', color: '#fb923c', bg: 'rgba(251,146,60,0.15)' },
        ppt: { abbr: 'PPT', label: 'PowerPoint', color: '#fb923c', bg: 'rgba(251,146,60,0.15)' },
        txt: { abbr: 'TXT', label: 'Text File', color: '#a78bfa', bg: 'rgba(167,139,250,0.15)' },
        md: { abbr: 'MD', label: 'Markdown', color: '#a78bfa', bg: 'rgba(167,139,250,0.15)' },
        json: { abbr: 'JSON', label: 'JSON File', color: '#fb923c', bg: 'rgba(251,146,60,0.15)' },
        py: { abbr: 'PY', label: 'Python File', color: '#60a5fa', bg: 'rgba(96,165,250,0.15)' },
        sql: { abbr: 'SQL', label: 'SQL File', color: '#f472b6', bg: 'rgba(244,114,182,0.15)' },
    };
    return map[ext] ?? { abbr: (ext.toUpperCase().slice(0, 4) || 'FILE'), label: 'File', color: '#94a3b8', bg: 'rgba(148,163,184,0.15)' };
}
const FileViewerModal: React.FC<{
    name: string;
    url: string;
    onClose: () => void;
}> = ({ name, url, onClose }) => {
    const ext = name.split('.').pop()?.toLowerCase() ?? '';
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
    const isPdf = ext === 'pdf';
    React.useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape')
            onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);
    return (<div className="cac-viewer-overlay" onClick={onClose}>
      <div className="cac-viewer-modal" onClick={e => e.stopPropagation()}>
        <div className="cac-viewer-header">
          <div className="cac-viewer-title">
            <FiFileText size={13}/>
            <span>{name}</span>
          </div>
          <div className="cac-viewer-actions">
            <a className="cac-viewer-icon-btn" href={url} target="_blank" rel="noopener noreferrer" title="Open in new tab">
              <IcoExternalLink size={13}/>
            </a>
            <button className="cac-viewer-icon-btn" onClick={onClose} title="Close">
              <FiX size={13}/>
            </button>
          </div>
        </div>
        <div className="cac-viewer-body">
          {isImage ? (<img src={url} alt={name} className="cac-viewer-img"/>) : isPdf ? (<iframe src={url} className="cac-viewer-frame" title={name}/>) : (<div className="cac-viewer-unsupported">
              <FiFileText size={36} style={{ opacity: 0.25 }}/>
              <p>Preview not available for this file type.</p>
              <a href={url} target="_blank" rel="noopener noreferrer" className="cac-viewer-open-link">
                Open file <IcoExternalLink size={11}/>
              </a>
            </div>)}
        </div>
      </div>
    </div>);
};
const FileChip: React.FC<{
    name: string;
    url: string;
    onRemove: () => void;
    onView: () => void;
}> = ({ name, onRemove, onView }) => {
    const ti = fileTypeInfo(name);
    const short = name.length > 30 ? name.slice(0, 27) + '…' : name;
    return (<div className="cac-file-chip">
      <div className="cac-file-chip__body" onClick={onView} title={`Preview ${name}`}>
        <div className="cac-file-chip__type-block" style={{ background: ti.bg, color: ti.color }}>
          <span className="cac-file-chip__abbr">{ti.abbr}</span>
        </div>
        <div className="cac-file-chip__meta">
          <span className="cac-file-chip__fname">{short}</span>
          <span className="cac-file-chip__label">{ti.label}</span>
        </div>
        <div className="cac-file-chip__view"><IcoEye size={11}/></div>
      </div>
      <button className="cac-file-chip__rm" onClick={e => { e.stopPropagation(); onRemove(); }} title="Remove">
        <FiX size={10}/>
      </button>
    </div>);
};
interface Props {
    agentId: string;
}
const ClaudeAgentChatPage: React.FC<Props> = ({ agentId }) => {
    const { user } = useAuth();
    const { setSelectedInsuredId } = useInsuredDetail();
    const { setSelectedPolicyId } = usePolicyDetail();
    useEffect(() => {
        _handleEntityClick = (type, id) => {
            if (type === 'insured') {
                setSelectedPolicyId(null);
                setSelectedInsuredId(id);
            }
            else {
                setSelectedInsuredId(null);
                setSelectedPolicyId(id);
            }
        };
        return () => { _handleEntityClick = null; };
    }, [setSelectedInsuredId, setSelectedPolicyId]);
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState('');
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [streaming, setStreaming] = useState(false);
    const [agent, setAgent] = useState<AgentDetail | null>(null);
    const [toolLog, setToolLog] = useState<ToolLogEntry[]>([]);
    const [showHistory, setShowHistory] = useState(false);
    const [toolApprovals, setToolApprovals] = useState<Record<string, ToolApprovalMode>>({});
    const toolApprovalsRef = useRef<Record<string, ToolApprovalMode>>({});
    const sessionIdRef = useRef<string | null>(null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
    const [uploading, setUploading] = useState(false);
    const [attachError, setAttachError] = useState('');
    const [viewFile, setViewFile] = useState<PendingAttachment | null>(null);
    const [downloadingArtifactId, setDownloadingArtifactId] = useState<string | null>(null);
    const [attachMenuOpen, setAttachMenuOpen] = useState(false);
    const [showAmsSelector, setShowAmsSelector] = useState(false);
    const attachMenuRef = useRef<HTMLDivElement>(null);
    const { theme } = useTheme();
    const abortRef = useRef<AbortController | null>(null);
    const bottomRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const historyRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const typewriterRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const animateMessage = useCallback((msgId: string, fullText: string) => {
        if (typewriterRef.current)
            clearTimeout(typewriterRef.current);
        if (!fullText)
            return;
        const chunkSize = Math.max(2, Math.ceil(fullText.length / 100));
        let pos = 0;
        const tick = () => {
            pos = Math.min(pos + chunkSize, fullText.length);
            const slice = fullText.slice(0, pos);
            const done = pos >= fullText.length;
            setMessages(prev => prev.map(m => m.id === msgId ? { ...m, content: slice, streaming: !done, isThinking: false } : m));
            if (!done) {
                typewriterRef.current = setTimeout(tick, 8);
            }
            else {
                typewriterRef.current = null;
            }
        };
        tick();
    }, []);
    // AI-112 Part 2: click-to-download for an artifact chip. Fetches the file
    // through MyAMP's authenticated Hermes-artifact proxy, then reuses
    // FileViewerModal (same preview/open-in-new-tab UI as every other file
    // chip in this page) against a temporary object URL — revoked when the
    // modal closes or another file replaces it (see the FileViewerModal
    // render below). Best-effort: a failed download just leaves the chip
    // clickable again rather than surfacing an error into the chat.
    const handleArtifactClick = useCallback(async (art: {
        name: string;
        artifactId: string;
    }) => {
        if (downloadingArtifactId)
            return;
        setDownloadingArtifactId(art.artifactId);
        try {
            const result = await downloadHermesArtifact(agentId, art.artifactId);
            if (!result)
                return;
            const objectUrl = URL.createObjectURL(result.blob);
            setViewFile(prev => {
                if (prev?.url.startsWith('blob:'))
                    URL.revokeObjectURL(prev.url);
                return { name: result.filename || art.name, url: objectUrl };
            });
        }
        finally {
            setDownloadingArtifactId(null);
        }
    }, [agentId, downloadingArtifactId]);
    const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);
        e.target.value = '';
        setAttachError('');
        if (!files.length)
            return;
        setUploading(true);
        const toAdd: PendingAttachment[] = [];
        for (const f of files) {
            try {
                const res = await uploadFile(f, agentId);
                toAdd.push({ name: res.fileName, url: res.fileUrl });
            }
            catch {
                setAttachError(`Failed to upload ${f.name}`);
            }
        }
        setUploading(false);
        if (toAdd.length)
            setAttachments(prev => [...prev, ...toAdd]);
    }, [agentId]);
    const handleAmsFilesUpload = useCallback(async (selections: AmsUploadSelection[]) => {
        if (!selections.length)
            return;
        setAttachError('');
        setUploading(true);
        try {
            const refs = await uploadAmsFiles(selections, agentId);
            setAttachments(prev => [...prev, ...refs.map(r => ({ name: r.name, url: r.url }))]);
        }
        finally {
            setUploading(false);
        }
    }, [agentId]);
    useEffect(() => {
        if (!attachMenuOpen)
            return;
        const handler = (e: MouseEvent) => {
            if (attachMenuRef.current && !attachMenuRef.current.contains(e.target as Node)) {
                setAttachMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [attachMenuOpen]);
    useEffect(() => {
        if (!showHistory)
            return;
        const handler = (e: MouseEvent) => {
            if (historyRef.current && !historyRef.current.contains(e.target as Node)) {
                setShowHistory(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showHistory]);
    const { mentionActive, mentionQuery, mentionResults, mentionLoading, mentionActiveIdx, setMentionActiveIdx, insertedNames, insertedEntities, clearInsertedNames, onValueChange, onKeyDown: mentionKeyDown, selectMention, closeMention, } = useInsuredMention();
    const renderWithEntityTokens = useCallback((text: string): React.ReactNode => {
        const segments: React.ReactNode[] = [];
        let last = 0;
        let idx = 0;
        ENTITY_TOKEN_RE.lastIndex = 0;
        let m: RegExpExecArray | null;
        while ((m = ENTITY_TOKEN_RE.exec(text)) !== null) {
            if (m.index > last) {
                segments.push(<React.Fragment key={idx++}>{renderMd(text.slice(last, m.index))}</React.Fragment>);
            }
            if (m[1] === 'IDF') {
                const fallback = m[2].trim();
                const id = m[3].trim();
                const chipIdx = idx++;
                segments.push(<InsuredChip key={chipIdx} id={id} fallback={fallback} onClick={() => { setSelectedPolicyId(null); setSelectedInsuredId(id); }}/>);
            }
            else {
                const num = m[5].trim();
                const id = m[6].trim();
                segments.push(<button key={idx++} className="cac-entity-link cac-entity-link--policy" onClick={() => { setSelectedInsuredId(null); setSelectedPolicyId(id); }}>
            <IcoEntityPolicy />{num}
          </button>);
            }
            last = m.index + m[0].length;
        }
        if (last < text.length) {
            segments.push(<React.Fragment key={idx++}>{renderMd(text.slice(last))}</React.Fragment>);
        }
        return segments.length === 0 ? null : segments;
    }, [setSelectedInsuredId, setSelectedPolicyId]);
    const renderUserContent = useCallback((text: string): React.ReactNode => {
        if (!USER_XML_ENTITY_RE.test(text))
            return renderMd(text);
        USER_XML_ENTITY_RE.lastIndex = 0;
        const segments: React.ReactNode[] = [];
        let last = 0;
        let idx = 0;
        let m: RegExpExecArray | null;
        const re = new RegExp(USER_XML_ENTITY_RE.source, 'g');
        while ((m = re.exec(text)) !== null) {
            if (m.index > last) {
                segments.push(<React.Fragment key={idx++}>{renderMd(text.slice(last, m.index))}</React.Fragment>);
            }
            const parsed = parseXmlEntity(m[0]);
            if (parsed && parsed.name) {
                if (parsed.type === 'policy') {
                    segments.push(<button key={idx++} className="cac-entity-link cac-entity-link--policy" onClick={() => { setSelectedInsuredId(null); setSelectedPolicyId(parsed.id); }}>
              <IcoEntityPolicy />{parsed.name}
            </button>);
                }
                else {
                    segments.push(<button key={idx++} className="cac-entity-link cac-entity-link--insured" onClick={() => { setSelectedPolicyId(null); setSelectedInsuredId(parsed.id); }}>
              <IcoEntityInsured />{parsed.name}
            </button>);
                }
            }
            last = m.index + m[0].length;
        }
        if (last < text.length) {
            segments.push(<React.Fragment key={idx++}>{renderMd(text.slice(last))}</React.Fragment>);
        }
        return segments.length === 0 ? renderMd(text) : <>{segments}</>;
    }, [setSelectedInsuredId, setSelectedPolicyId]);
    const mirrorSegments = React.useMemo(() => {
        if (!insertedNames.length)
            return null;
        const escaped = insertedNames.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
        const regex = new RegExp(`(${escaped.join('|')})`, 'g');
        const parts = input.split(regex);
        const nameSet = new Set(insertedNames);
        return parts.map((part, i) => nameSet.has(part)
            ? <span key={i} className="cac-mention-token">{part}</span>
            : <span key={i}>{part}</span>);
    }, [input, insertedNames]);
    const displayName = user?.name ?? '';
    useEffect(() => { getAgent(agentId).then(setAgent).catch(() => { }); }, [agentId]);
    useEffect(() => {
        getToolApprovals(agentId)
            .then(data => { setToolApprovals(data); toolApprovalsRef.current = data; })
            .catch(() => { });
    }, [agentId]);
    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
    useEffect(() => { toolApprovalsRef.current = toolApprovals; }, [toolApprovals]);
    useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);
    // Set when the user presses Stop for the in-flight turn; read once by the
    // idle handlers so the bubble is finished as "stopped", not failed.
    const interruptedRef = useRef(false);
    // Stop the agent's in-flight turn (POST /chat/interrupt). The session runs
    // to a safe boundary and goes idle; the open stream then ends normally with
    // whatever text arrived.
    const handleInterrupt = useCallback(async () => {
        const sid = sessionIdRef.current ?? sessionId;
        if (!sid || !streaming) return;
        interruptedRef.current = true;
        try {
            await interruptChat(sid);
        }
        catch (err) {
            // Usually a 409: the turn finished before the click landed.
            interruptedRef.current = false;
            console.warn('interruptChat failed', err);
        }
    }, [sessionId, streaming]);
    useEffect(() => {
        const pending = messages.filter(m => m.role === 'tool' && m.toolStatus === 'pending' && !!m.toolEventId);
        if (pending.length === 0)
            return;
        const approvals = toolApprovalsRef.current;
        for (const tool of pending) {
            const key = tool.mcpServer ? `${tool.mcpServer}::${tool.toolName}` : `::${tool.toolName ?? ''}`;
            const mode = approvals[key] ?? 'ask';
            if (mode === 'approved') {
                handleToolAction(tool.toolEventId!, true, tool.toolSessionThreadId ?? null);
                break;
            }
            else if (mode === 'restrict') {
                handleToolAction(tool.toolEventId!, false, tool.toolSessionThreadId ?? null);
                break;
            }
        }
    }, [messages]);
    useEffect(() => {
        window.dispatchEvent(new CustomEvent('updateToolLog', { detail: { entries: toolLog } }));
    }, [toolLog]);
    useEffect(() => {
        const el = textareaRef.current;
        if (!el)
            return;
        el.style.height = 'auto';
        el.style.height = Math.min(Math.max(el.scrollHeight, 48), 160) + 'px';
    }, [input]);
    const send = useCallback(async (overrideText?: string) => {
        const text = (overrideText ?? input).trim();
        if ((!text && attachments.length === 0) || streaming)
            return;
        const pending = [...attachments];
        let apiText = text;
        if (insertedEntities.size > 0) {
            insertedEntities.forEach((entity, name) => {
                apiText = apiText.replace(name, buildEntityXmlTag(entity));
            });
        }
        const fileUrls = pending.map(a => a.url);
        const userId = `u-${Date.now()}`;
        const asstId = `a-${Date.now()}`;
        const displayContent = [
            text,
            ...pending.map(a => `📎 ${a.name}`),
        ].filter(Boolean).join('\n');
        setMessages(prev => [
            ...prev,
            { id: userId, role: 'user', content: displayContent, fileAttachments: pending.length ? pending : undefined },
            { id: asstId, role: 'assistant', content: '', streaming: true },
        ]);
        setInput('');
        setAttachments([]);
        setAttachError('');
        clearInsertedNames();
        setStreaming(true);
        abortRef.current?.abort();
        abortRef.current = new AbortController();
        if (typewriterRef.current) {
            clearTimeout(typewriterRef.current);
            typewriterRef.current = null;
        }
        try {
            const curRef = { id: asstId };
            await streamChat(apiText, sessionId, (evt: SseEvent) => {
                if (evt.type === 'session.start') {
                    setSessionId(evt.session_id);
                }
                else if (evt.type === 'agent.thinking' || evt.type === 'span.model_request_start') {
                    setMessages(prev => prev.map(m => m.id === curRef.id ? { ...m, isThinking: true } : m));
                }
                else if (evt.type === 'agent.mcp_tool_use' ||
                    evt.type === 'agent.tool_use' ||
                    evt.type === 'agent.custom_tool_use') {
                    const toolId = `tool-${Date.now()}-${Math.random()}`;
                    const nowMs = Date.now();
                    const mcpSrv = (evt as {
                        mcp_server_name?: string;
                    }).mcp_server_name;
                    const thrId = (evt as {
                        session_thread_id?: string | null;
                    }).session_thread_id ?? null;
                    setMessages(prev => [
                        ...prev.map(m => m.id === curRef.id ? { ...m, isThinking: false } : m),
                        { id: toolId, role: 'tool' as const, content: '', toolName: evt.name, toolInput: evt.input, toolStatus: 'running' as const, toolEventId: evt.id, toolSessionThreadId: thrId, calledAt: nowMs, mcpServer: mcpSrv },
                    ]);
                    setToolLog(prev => [...prev, { id: toolId, name: evt.name, input: evt.input, status: 'running', calledAt: nowMs, mcpServer: mcpSrv }]);
                }
                else if (evt.type === 'agent.message') {
                    const full = (evt.content ?? []).filter(c => c.type === 'text').map(c => c.text ?? '').join('');
                    const doneNow = Date.now();
                    const finId = curRef.id;
                    const nextId = `a-${Date.now()}-cont`;
                    curRef.id = nextId;
                    setMessages(prev => {
                        const updated = prev.map(m => {
                            if (m.role === 'tool' && m.toolStatus === 'running')
                                return { ...m, toolStatus: 'done' as const, completedAt: doneNow };
                            if (m.id === finId)
                                return { ...m, content: '', isThinking: false, streaming: true };
                            return m;
                        });
                        return [...updated, { id: nextId, role: 'assistant' as const, content: '', streaming: true }];
                    });
                    setToolLog(prev => prev.map(e => e.status === 'running' ? { ...e, status: 'done', completedAt: doneNow } : e));
                    if (full)
                        animateMessage(finId, full);
                }
                else if (evt.type === 'assistant.message' || evt.type === 'message.delta') {
                    const chunk = (evt.content ?? []).filter(c => c.type === 'text').map(c => c.text ?? '').join('');
                    const doneNow = Date.now();
                    if (chunk) {
                        setMessages(prev => prev.map(m => {
                            if (m.role === 'tool' && m.toolStatus === 'running')
                                return { ...m, toolStatus: 'done' as const, completedAt: doneNow };
                            if (m.id === curRef.id)
                                return { ...m, content: m.content + chunk, isThinking: false };
                            return m;
                        }));
                        setToolLog(prev => prev.map(e => e.status === 'running' ? { ...e, status: 'done', completedAt: doneNow } : e));
                    }
                }
                else if (evt.type === 'session.status_idle' || evt.type === 'session.thread_status_idle') {
                    const stopEvt = evt as {
                        stop_reason?: {
                            type: string;
                            event_ids?: string[];
                        } | null;
                    };
                    const stopType = stopEvt.stop_reason?.type ?? '';
                    const pendingIds = new Set(stopEvt.stop_reason?.event_ids ?? []);
                    const lastId = curRef.id;
                    if (stopType === 'requires_action' && pendingIds.size > 0) {
                        setMessages(prev => prev.map(m => {
                            if (m.role === 'tool' && m.toolEventId && pendingIds.has(m.toolEventId))
                                return { ...m, toolStatus: 'pending' as const };
                            if (m.id === lastId)
                                return { ...m, streaming: false, isThinking: false };
                            return m;
                        }));
                    }
                    else {
                        const failed = stopType === 'retries_exhausted' || stopType === 'error';
                        // Stop pressed this turn: whatever streamed is the answer, and
                        // an empty bubble is kept to carry the "stopped" note.
                        const stopped = interruptedRef.current;
                        interruptedRef.current = false;
                        setMessages(prev => {
                            const last = prev[prev.length - 1];
                            // Don't drop a trailing empty assistant bubble if it's
                            // carrying an artifact.created chip (AI-112 Part 2) — an
                            // artifact-only turn has no text, but the chip still
                            // needs a message to render on.
                            const base = (!stopped && last?.id === lastId && !last?.content && !last?.artifacts?.length) ? prev.slice(0, -1) : prev;
                            return base.map(m => {
                                if (m.role === 'tool' && m.toolStatus === 'running')
                                    return { ...m, toolStatus: 'done' as const };
                                if (m.id !== lastId)
                                    return m;
                                if (stopped)
                                    return { ...m, content: m.content || 'Response stopped.', isStopped: true, isError: false, isThinking: false, streaming: false };
                                if (failed && !m.content && !m.artifacts?.length)
                                    return { ...m, content: "I couldn't complete that request. Please try again.", isError: true, isThinking: false, streaming: false };
                                return { ...m, isThinking: false, streaming: false };
                            });
                        });
                        if (evt.type === 'session.status_idle')
                            setStreaming(false);
                    }
                }
                else if (evt.type === 'artifact.created') {
                    // A file the agent created this turn (Hermes or Claude
                    // upload_session_file) — attach it to whichever assistant
                    // message is currently in flight so it renders as a
                    // download chip on that message.
                    const name = evt.filename || 'Generated file';
                    const artifactId = evt.artifact_id;
                    if (artifactId) {
                        setMessages(prev => prev.map(m => m.id === curRef.id
                            ? { ...m, artifacts: [...(m.artifacts ?? []), { name, artifactId }] }
                            : m));
                    }
                }
                else if (evt.type === 'error') {
                    // Fatal turn error (BUDGET_REACHED, SESSION_TERMINATED, ...).
                    // Whatever streamed before is kept with the reason under it.
                    // The status_idle handler above may already have dropped the
                    // empty assistant bubble (no answer text arrived), so append
                    // the error as its own message rather than losing it. A turn
                    // the user stopped before any text produced an "empty
                    // response" error on the backend — that is not a failure
                    // here, unless the session was actually terminated.
                    setMessages(prev => {
                        const cur = prev.find(m => m.id === curRef.id);
                        const terminated = !!evt.terminated || evt.code === SESSION_TERMINATED_CODE;
                        const stopped = (interruptedRef.current || !!cur?.isStopped) && !terminated;
                        const errText = stopped ? 'Response stopped.' : sseErrorMessage(evt);
                        if (!cur)
                            return [...prev, { id: curRef.id, role: 'assistant' as const, content: errText, isError: !stopped, isStopped: stopped, isTerminated: terminated, streaming: false }];
                        return prev.map(m => m.id !== curRef.id ? m : {
                            ...m,
                            content: stopped ? (m.content || errText) : (m.content ? `${m.content}\n\n⚠️ ${errText}` : errText),
                            isError: !stopped,
                            isStopped: stopped,
                            isTerminated: terminated,
                            isThinking: false,
                            streaming: false,
                        });
                    });
                    setStreaming(false);
                }
            }, abortRef.current.signal, text, fileUrls.length ? fileUrls : undefined, pending.map(a => a.name));
        }
        catch (err: unknown) {
            if ((err as Error)?.name === 'AbortError')
                return;
            setMessages(prev => prev.map(m => m.id === asstId
                ? { ...m, content: 'Something went wrong. Please try again.', isError: true, streaming: false }
                : m));
            setStreaming(false);
        }
    }, [input, sessionId, streaming, insertedEntities, clearInsertedNames, animateMessage]);
    const handleToolAction = useCallback(async (toolEventId: string, approve: boolean, sessionThreadId?: string | null) => {
        const sid = sessionIdRef.current ?? sessionId;
        if (!sid)
            return;
        setMessages(prev => prev.map(m => m.toolEventId === toolEventId
            ? { ...m, toolStatus: (approve ? 'approved' : 'denied') as ToolStatus }
            : m));
        if (!approve) {
            setStreaming(false);
            return;
        }
        const resumeAsstId = `a-resume-${Date.now()}`;
        setMessages(prev => [...prev, { id: resumeAsstId, role: 'assistant' as const, content: '', streaming: true }]);
        setStreaming(true);
        abortRef.current?.abort();
        abortRef.current = new AbortController();
        try {
            const resumeCurRef = { id: resumeAsstId };
            await resumeWithAction(sid, [toolEventId], true, (evt: SseEvent) => {
                if (evt.type === 'agent.thinking' || evt.type === 'span.model_request_start') {
                    setMessages(prev => prev.map(m => m.id === resumeCurRef.id ? { ...m, isThinking: true } : m));
                }
                else if (evt.type === 'agent.mcp_tool_use' ||
                    evt.type === 'agent.tool_use' ||
                    evt.type === 'agent.custom_tool_use') {
                    const toolId = `tool-${Date.now()}-${Math.random()}`;
                    const nowMs = Date.now();
                    const mcpSrv = (evt as {
                        mcp_server_name?: string;
                    }).mcp_server_name;
                    const thrId = (evt as {
                        session_thread_id?: string | null;
                    }).session_thread_id ?? null;
                    setMessages(prev => [
                        ...prev.map(m => m.id === resumeCurRef.id ? { ...m, isThinking: false } : m),
                        { id: toolId, role: 'tool' as const, content: '', toolName: evt.name, toolInput: evt.input, toolStatus: 'running' as ToolStatus, toolEventId: evt.id, toolSessionThreadId: thrId, calledAt: nowMs, mcpServer: mcpSrv },
                    ]);
                    setToolLog(prev => [...prev, { id: toolId, name: evt.name, input: evt.input, status: 'running', calledAt: nowMs, mcpServer: mcpSrv }]);
                }
                else if (evt.type === 'agent.message') {
                    const full = (evt.content ?? []).filter(c => c.type === 'text').map(c => c.text ?? '').join('');
                    const doneNow = Date.now();
                    const finId = resumeCurRef.id;
                    const nextId = `a-${Date.now()}-cont`;
                    resumeCurRef.id = nextId;
                    setMessages(prev => {
                        const updated = prev.map(m => {
                            if (m.role === 'tool' && m.toolStatus === 'running')
                                return { ...m, toolStatus: 'done' as ToolStatus, completedAt: doneNow };
                            if (m.id === finId)
                                return { ...m, content: full || m.content, isThinking: false, streaming: false };
                            return m;
                        });
                        return [...updated, { id: nextId, role: 'assistant' as const, content: '', streaming: true }];
                    });
                    setToolLog(prev => prev.map(e => e.status === 'running' ? { ...e, status: 'done', completedAt: doneNow } : e));
                }
                else if (evt.type === 'artifact.created') {
                    const name = evt.filename || 'Generated file';
                    const artifactId = evt.artifact_id;
                    if (artifactId) {
                        setMessages(prev => prev.map(m => m.id === resumeCurRef.id
                            ? { ...m, artifacts: [...(m.artifacts ?? []), { name, artifactId }] }
                            : m));
                    }
                }
                else if (evt.type === 'session.thread_status_idle') {
                    const stopEvt = evt as {
                        stop_reason?: {
                            type: string;
                            event_ids?: string[];
                        } | null;
                    };
                    const stopType = stopEvt.stop_reason?.type ?? '';
                    const pendingIds = new Set(stopEvt.stop_reason?.event_ids ?? []);
                    const lastId = resumeCurRef.id;
                    if (stopType === 'requires_action' && pendingIds.size > 0) {
                        setMessages(prev => prev.map(m => {
                            if (m.role === 'tool' && m.toolEventId && pendingIds.has(m.toolEventId))
                                return { ...m, toolStatus: 'pending' as ToolStatus };
                            if (m.id === lastId)
                                return { ...m, streaming: false, isThinking: false };
                            return m;
                        }));
                    }
                    else {
                        setMessages(prev => {
                            const last = prev[prev.length - 1];
                            // See the matching comment in send()'s idle handler — keep
                            // a trailing empty bubble if it carries an artifact chip.
                            const base = (last?.id === lastId && !last?.content && !last?.artifacts?.length) ? prev.slice(0, -1) : prev;
                            return base.map(m => {
                                if (m.role === 'tool' && m.toolStatus === 'running')
                                    return { ...m, toolStatus: 'done' as ToolStatus };
                                if (m.id === lastId)
                                    return { ...m, streaming: false, isThinking: false };
                                return m;
                            });
                        });
                        setStreaming(false);
                    }
                }
                else if (evt.type === 'session.status_idle') {
                    const lastId = resumeCurRef.id;
                    // Stop pressed during the resumed turn: keep the bubble and
                    // mark it stopped (see the matching send() idle handler).
                    const stopped = interruptedRef.current;
                    interruptedRef.current = false;
                    setMessages(prev => {
                        const last = prev[prev.length - 1];
                        const base = (!stopped && last?.id === lastId && !last?.content && !last?.artifacts?.length) ? prev.slice(0, -1) : prev;
                        return base.map(m => m.id === lastId
                            ? (stopped
                                ? { ...m, content: m.content || 'Response stopped.', isStopped: true, streaming: false, isThinking: false }
                                : { ...m, streaming: false, isThinking: false })
                            : m);
                    });
                    setStreaming(false);
                }
            }, abortRef.current.signal, sessionThreadId != null ? [sessionThreadId] : undefined);
        }
        catch (err: unknown) {
            if ((err as Error)?.name !== 'AbortError')
                setStreaming(false);
        }
    }, [sessionId]);
    const handleKeyDown = (e: React.KeyboardEvent) => {
        const consumed = mentionKeyDown(e, input, (val) => setInput(val));
        if (consumed)
            return;
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };
    const handleResumeSession = useCallback((sid: string, _title: string) => {
        abortRef.current?.abort();
        if (typewriterRef.current) {
            clearTimeout(typewriterRef.current);
            typewriterRef.current = null;
        }
        setSessionId(sid);
        setStreaming(false);
        setInput('');
        setToolLog([]);
        window.dispatchEvent(new CustomEvent('clearToolLog'));
        setMessages([]);
        setHistoryLoading(true);
        getSessionMessages(sid)
            .then((rows: ChatMessageRecord[]) => {
            setHistoryLoading(false);
            if (rows.length === 0) {
                setMessages([]);
                return;
            }
            setMessages(rows.map((r, i) => {
                const files = r.file_data || [];
                const content = files.length
                    ? r.content + '\n' + files.map(f => `📎 ${f.name}`).join('\n')
                    : r.content;
                return {
                    id: `hist-${i}`,
                    role: r.role as 'user' | 'assistant',
                    content,
                    fileAttachments: files.length ? files : undefined,
                };
            }));
        })
            .catch(() => {
            setHistoryLoading(false);
            setMessages([{ id: 'hist-err', role: 'assistant', content: '_Could not load history._' }]);
        });
    }, []);
    const handleNewChat = useCallback(() => {
        abortRef.current?.abort();
        if (typewriterRef.current) {
            clearTimeout(typewriterRef.current);
            typewriterRef.current = null;
        }
        setMessages([]);
        setSessionId(null);
        setStreaming(false);
        setInput('');
        setToolLog([]);
        window.dispatchEvent(new CustomEvent('clearToolLog'));
        closeMention();
        clearInsertedNames();
        setTimeout(() => textareaRef.current?.focus(), 50);
    }, [closeMention, clearInsertedNames]);
    useEffect(() => {
        window.addEventListener('newAgentChat', handleNewChat);
        return () => window.removeEventListener('newAgentChat', handleNewChat);
    }, [handleNewChat]);
    const canSend = (!!input.trim() || attachments.length > 0) && !streaming && !uploading;
    const pendingTools = messages.filter(m => m.role === 'tool' && m.toolStatus === 'pending');
    const topAttachments = React.useMemo(() => {
        const seen = new Set<string>();
        const rows: PendingAttachment[] = [];
        for (const msg of messages) {
            const candidates: PendingAttachment[] = [
                ...(msg.fileAttachments ?? []),
                ...extractAttachedFilesFromText(msg.content || ''),
            ];
            for (const file of candidates) {
                const name = (file.name || '').trim();
                if (!name)
                    continue;
                const key = `${name.toLowerCase()}|${(file.url || '').trim().toLowerCase()}`;
                if (seen.has(key))
                    continue;
                seen.add(key);
                rows.push({ name, url: (file.url || '').trim() });
            }
        }
        return rows;
    }, [messages]);
    return (<div className="cac-root">
      {viewFile && (<FileViewerModal name={viewFile.name} url={viewFile.url} onClose={() => {
                if (viewFile.url.startsWith('blob:'))
                    URL.revokeObjectURL(viewFile.url);
                setViewFile(null);
            }}/>)}
      <main className="cac-main">

        
        <div className="cac-scroll">
          <div className="cac-feed">
            
            {historyLoading && (<div className="cac-hist-skel">
                {[56, 96, 72, 80, 64].map((h, i) => (<div key={i} className="cac-turn-group cac-hist-skel__group">
                    <div className="cac-turn-group__left">
                      <div className="cac-hist-skel__avatar"/>
                    </div>
                    <div className="cac-turn-group__msgs">
                      <div className="cac-hist-skel__chip" style={{ height: h }}/>
                    </div>
                  </div>))}
              </div>)}

            
            {!historyLoading && messages.length === 0 && (<motion.div className="cac-welcome" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}>

                
                <div className="cac-welcome__glow"/>
                <div className="cac-welcome__glow cac-welcome__glow--purple"/>

                
                <div className="cac-welcome__hero">
                  <div className="cac-greeting__logo">
                    <ClaudeMark size={32}/>
                    <div className="cac-greeting__logo-ring"/>
                  </div>
                  <div className="cac-welcome__hero-text">
                    <h1 className="cac-greeting__title">
                      {getGreeting()}{displayName ? `, ${displayName}` : ''}
                    </h1>
                    <p className="cac-greeting__date">{getTodayLabel()}</p>
                  </div>
                  <div className="cac-greeting__badge">
                    <span className="cac-greeting__badge-dot"/>
                    <RiSparklingFill size={11}/>
                    {agent?.name ?? 'Claude Agent'}
                  </div>
                </div>

                
                <div className="cac-welcome__body">

                  
                  <div className="cac-welcome__left">
                    <p className="cac-welcome__desc">
                      {agent?.description || 'Your autonomous AI agent built for insurance agencies — powered by Anthropic Claude and embedded directly in your Momentum AMP workspace.'}
                    </p>
                    <div className="cac-welcome__features">
                      {[
                { icon: <FiZap size={15}/>, color: '#fb923c', title: 'Autonomous Tools', desc: 'Executes real tasks end-to-end — look up insureds, pull policies, file claims, and more using 30+ built-in AMS tools.' },
                { icon: <FiSettings size={15}/>, color: '#818cf8', title: 'MCP Server Connections', desc: 'Connects securely to external portals and APIs via Model Context Protocol, extending your agent\'s reach beyond the AMS.' },
                { icon: <FiFileText size={15}/>, color: '#34d399', title: 'Document Intelligence', desc: 'Upload policies, loss runs, or any insurance document and let the agent extract, analyze, and act on the data instantly.' },
                { icon: <FiLock size={15}/>, color: '#f472b6', title: 'Human-in-the-Loop', desc: 'You stay in control — sensitive actions pause for your approval before execution. Enterprise-grade security throughout.' },
            ].map((f, i) => (<motion.div key={i} className="cac-welcome__feature-card" initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.08 }} style={{ '--fc-color': f.color } as React.CSSProperties}>
                          <div className="cac-welcome__fc-icon">{f.icon}</div>
                          <div className="cac-welcome__fc-body">
                            <div className="cac-welcome__fc-title">{f.title}</div>
                            <div className="cac-welcome__fc-desc">{f.desc}</div>
                          </div>
                        </motion.div>))}
                    </div>
                  </div>

                  
                  <div className="cac-welcome__right">
                    <div className="cac-welcome__section-label">
                      <span>Quick Actions</span>
                    </div>
                    <div className="cac-suggestions cac-suggestions--col">
                      {SUGGESTIONS.map((s, i) => (<motion.button key={i} className="cac-suggestion" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 + i * 0.07 }} onClick={() => send(s.text)}>
                          <span className="cac-suggestion__icon"><s.Icon size={14}/></span>
                          <span className="cac-suggestion__text">{s.text}</span>
                        </motion.button>))}
                    </div>

                    <WelcomeRecentSessions onResume={handleResumeSession}/>
                  </div>

                </div>
              </motion.div>)}

            
            {!historyLoading && topAttachments.length > 0 && (<>
              <div style={{ position: 'sticky', top: 0, left: 0, zIndex: 1200, background: 'var(--bg-primary)', padding: '6px var(--space-5) 10px var(--space-5)', marginLeft: 'calc(-1 * var(--space-5))', marginBottom: '10px', width: 'calc(100% + (2 * var(--space-5)))' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', background: 'transparent', border: 'none', borderRadius: 0, padding: 0, boxShadow: 'none', maxWidth: 'min(92vw, 980px)' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.07em', flexShrink: 0 }}>
                  Files
                </span>
                {topAttachments.map((file, idx) => {
                const displayName = decodeURIComponent((file.name || '').replace(/\+/g, ' '))
                    .replace(/\s*,\s*/g, ', ')
                    .replace(/\s+/g, ' ')
                    .trim();
                const canOpen = !!file.url;
                return (<button key={`dw-top-file-${displayName}-${idx}`} type="button" disabled={!canOpen} title={displayName} onClick={() => {
                        if (!file.url)
                            return;
                        setViewFile({ name: displayName, url: file.url });
                    }} style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        border: '1px solid var(--border-glass)',
                        borderRadius: 999,
                        padding: '4px 10px',
                        background: 'rgba(139,92,246,0.08)',
                        fontSize: 11,
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        cursor: canOpen ? 'pointer' : 'default',
                        maxWidth: 260,
                        opacity: canOpen ? 1 : 0.65,
                    }}>
                      <FiPaperclip size={11}/>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</span>
                    </button>);
            })}
              </div>
              </div>
              </>)}

            {!historyLoading && (() => {
            // A message with no text but a Hermes/Claude artifact.created chip
            // (AI-112 Part 2) still needs to render — treat it like non-empty
            // content everywhere "is this message empty" is asked below.
            const hasArtifacts = (m: Message) => !!(m.artifacts && m.artifacts.length);
            const visible = messages.filter(m => !(m.role === 'assistant' && !m.content && !m.streaming && !m.isThinking && !hasArtifacts(m)));
            const renderArtifactChips = (msg: Message) => msg.artifacts && msg.artifacts.length > 0 && (<div className="cac-msg-files">
                    {msg.artifacts.map((art, ai) => {
                    const ti = fileTypeInfo(art.name);
                    const isDownloading = downloadingArtifactId === art.artifactId;
                    return (<div key={ai} className="cac-msg-file-chip cac-msg-file-chip--clickable" onClick={() => handleArtifactClick(art)} title={`Download ${art.name}`} style={isDownloading ? { opacity: 0.6, pointerEvents: 'none' } : undefined}>
                          <div className="cac-msg-file-chip__icon" style={{ background: ti.bg, color: ti.color }}>
                            <span className="cac-msg-file-chip__abbr">{ti.abbr}</span>
                          </div>
                          <div className="cac-msg-file-chip__meta">
                            <span className="cac-msg-file-chip__name">{art.name.length > 32 ? art.name.slice(0, 29) + '…' : art.name}</span>
                            <span className="cac-msg-file-chip__type">{ti.label}</span>
                          </div>
                          <div className="cac-msg-file-chip__view"><IcoEye size={10}/></div>
                        </div>);
                })}
                  </div>);
            const renderBody = (msg: Message) => {
                if (!msg.content) {
                    if (msg.isThinking)
                        return <ThinkingIndicator />;
                    if (msg.streaming)
                        return <TypingDots />;
                    return hasArtifacts(msg) ? renderArtifactChips(msg) : null;
                }
                const lines = msg.content.split('\n');
                const textLines: string[] = [];
                const fileNames: string[] = [];
                lines.forEach(l => {
                    if (l.startsWith('📎 '))
                        fileNames.push(l.slice(3));
                    else
                        textLines.push(l);
                });
                const textPart = textLines.join('\n').trim();
                const urlMap = new Map<string, string>((msg.fileAttachments ?? []).map(a => [a.name, a.url]));
                const renderedText = textPart
                    ? msg.role === 'assistant' && !msg.streaming
                        ? renderWithEntityTokens(textPart)
                        : msg.role === 'assistant'
                            ? renderMd(stripEntityTokens(textPart))
                            : renderUserContent(textPart)
                    : null;
                return (<>
                    {renderedText}
                    {fileNames.length > 0 && (<div className="cac-msg-files">
                        {fileNames.map((fn, fi) => {
                            const ti = fileTypeInfo(fn);
                            const url = urlMap.get(fn);
                            return (<div key={fi} className={`cac-msg-file-chip${url ? ' cac-msg-file-chip--clickable' : ''}`} onClick={url ? () => setViewFile({ name: fn, url }) : undefined} title={url ? `Preview ${fn}` : fn}>
                              <div className="cac-msg-file-chip__icon" style={{ background: ti.bg, color: ti.color }}>
                                <span className="cac-msg-file-chip__abbr">{ti.abbr}</span>
                              </div>
                              <div className="cac-msg-file-chip__meta">
                                <span className="cac-msg-file-chip__name">{fn.length > 32 ? fn.slice(0, 29) + '…' : fn}</span>
                                <span className="cac-msg-file-chip__type">{ti.label}</span>
                              </div>
                              {url && <div className="cac-msg-file-chip__view"><IcoEye size={10}/></div>}
                            </div>);
                        })}
                      </div>)}
                    {renderArtifactChips(msg)}
                  </>);
            };
            type TurnGroup = {
                key: string;
                type: 'user' | 'agent';
                msgs: Message[];
            };
            const groups: TurnGroup[] = [];
            for (const msg of visible) {
                if (msg.role === 'user') {
                    groups.push({ key: msg.id, type: 'user', msgs: [msg] });
                }
                else {
                    const last = groups[groups.length - 1];
                    if (last?.type === 'agent') {
                        last.msgs.push(msg);
                    }
                    else {
                        groups.push({ key: msg.id, type: 'agent', msgs: [msg] });
                    }
                }
            }
            // Yes / No quick replies attach only to the final agent turn, once it has
            // settled, with no tool approval pending (that has its own Approve/Deny).
            const lastGroup = groups[groups.length - 1];
            const renderProceedReplies = (group: TurnGroup, msg: Message | null | undefined) =>
                group === lastGroup && msg && msg.role === 'assistant' && !msg.isError
                    && !streaming && pendingTools.length === 0 && asksToProceed(msg.content)
                    ? <ProceedQuickReplies onReply={(text) => { void send(text); }}/>
                    : null;
            // The AI session ended on the service side (SESSION_TERMINATED):
            // ask for a fresh start rather than leaving a dead-looking chat.
            const renderNewChatAsk = () => (
                <div className="cac-msg__new-chat-ask">
                    <button type="button" className="cac-msg__new-chat-btn" onClick={handleNewChat}>
                        <FiRefreshCw size={12}/> Start new chat
                    </button>
                </div>
            );
            return groups.map(group => {
                if (group.type === 'user') {
                    const msg = group.msgs[0];
                    return (<motion.div key={msg.id} className="cac-msg cac-msg--user" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.16 }}>
                      <div className="cac-msg__body">{renderBody(msg)}</div>
                    </motion.div>);
                }
                const turnIsLive = group.msgs.some(m => m.streaming || m.isThinking);
                const hasTools = group.msgs.some(m => m.role === 'tool');
                const asstMsgs = group.msgs.filter(m => m.role === 'assistant');
                const allAsstEmpty = asstMsgs.every(m => !m.content && !hasArtifacts(m));
                const useThinking = hasTools
                    || (allAsstEmpty && asstMsgs.some(m => m.isThinking || m.streaming));
                if (useThinking) {
                    const finalMsg = [...asstMsgs].reverse().find(m => !!m.content || hasArtifacts(m)) ?? null;
                    const showFinal = !!finalMsg && (!!finalMsg.content || hasArtifacts(finalMsg));
                    const thinkItems = showFinal ? group.msgs.filter(m => m !== finalMsg) : group.msgs;
                    return (<div key={group.key} className="cac-turn-group">
                      <div className="cac-turn-group__left">
                        <span className="cac-turn-group__avatar"><ClaudeMark size={22}/></span>
                        <div className="cac-turn-group__line"/>
                      </div>
                      <div className="cac-turn-group__msgs">
                        <ThinkingBlock items={thinkItems} isLive={turnIsLive}/>
                        {showFinal && finalMsg && (<motion.div className="cac-turn-msg" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
                            <div className={`cac-msg__body${finalMsg.isError ? ' cac-msg__body--error' : ''}${finalMsg.isStopped ? ' cac-msg__body--stopped' : ''}`}>
                              {renderBody(finalMsg)}
                            </div>
                            {finalMsg.isTerminated && renderNewChatAsk()}
                            {!turnIsLive && renderProceedReplies(group, finalMsg)}
                          </motion.div>)}
                      </div>
                    </div>);
                }
                const simpleMsg = asstMsgs[0] ?? group.msgs[0];
                return (<div key={group.key} className="cac-turn-group">
                    <div className="cac-turn-group__left">
                      <span className="cac-turn-group__avatar"><ClaudeMark size={22}/></span>
                    </div>
                    <div className="cac-turn-group__msgs">
                      <motion.div className="cac-turn-msg" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.16 }}>
                        <div className={`cac-msg__body${simpleMsg?.isError ? ' cac-msg__body--error' : ''}${simpleMsg?.isStopped ? ' cac-msg__body--stopped' : ''}`}>
                          {simpleMsg ? renderBody(simpleMsg) : null}
                        </div>
                        {simpleMsg?.isTerminated && renderNewChatAsk()}
                        {!turnIsLive && renderProceedReplies(group, simpleMsg)}
                      </motion.div>
                    </div>
                  </div>);
            });
        })()}

            <div ref={bottomRef} style={{ height: 1 }}/>

          </div>
        </div>

        
        <div className="cac-input-area">

          
          <ApprovalPanel tools={pendingTools} onApprove={(id, tid) => handleToolAction(id, true, tid)} onDeny={(id, tid) => handleToolAction(id, false, tid)}/>

          <div style={{ position: 'relative' }}>

            
            {mentionActive && (<div className="cac-mention-anchor">
                <InsuredMentionDropdown query={mentionQuery} results={mentionResults} loading={mentionLoading} activeIdx={mentionActiveIdx} onSelect={(ins) => {
                setInput(selectMention(ins, input));
                setTimeout(() => textareaRef.current?.focus(), 0);
            }} onActiveChange={setMentionActiveIdx}/>
              </div>)}

          
          <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.txt,.md,.csv,.json,.xml,.yaml,.yml,.ts,.tsx,.js,.jsx,.py,.sh,.sql,.docx,.xlsx,.pptx" style={{ display: 'none' }} onChange={handleFileSelect}/>

          
          {(attachments.length > 0 || uploading || attachError) && (<div className="cac-attachments">
              {uploading && (<div className="cac-file-chip cac-file-chip--uploading">
                  <div className="cac-file-chip__body" style={{ pointerEvents: 'none' }}>
                    <div className="cac-file-chip__type-block" style={{ background: 'rgba(148,163,184,0.15)', color: '#94a3b8' }}>
                      <span className="cac-file-chip__upload-spin"/>
                    </div>
                    <div className="cac-file-chip__meta">
                      <span className="cac-file-chip__fname">Uploading…</span>
                      <span className="cac-file-chip__label">Please wait</span>
                    </div>
                  </div>
                </div>)}
              {attachments.map((a, i) => (<FileChip key={i} name={a.name} url={a.url} onRemove={() => setAttachments(prev => prev.filter((_, j) => j !== i))} onView={() => setViewFile({ name: a.name, url: a.url })}/>))}
              {attachError && <span className="cac-attach-error">{attachError}</span>}
            </div>)}

          <div className="cac-input-box">

            
            <div ref={attachMenuRef} style={{ position: 'relative', display: 'flex' }}>
              <button className="cac-input-icon-btn" title="Attach file" disabled={streaming || uploading} onClick={() => setAttachMenuOpen(v => !v)}>
                <FiPaperclip size={15}/>
              </button>
              {attachMenuOpen && (<div style={{ position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, minWidth: 190, background: theme === 'dark' ? '#1e1e35' : '#ffffff', border: '1px solid var(--border-glass, rgba(255,255,255,0.09))', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.35)', zIndex: 50, overflow: 'hidden' }}>
                  <div style={{ padding: '8px 16px 4px', fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'Poppins, sans-serif' }}>Choose upload type:</div>
                  <button type="button" onClick={() => { setAttachMenuOpen(false); fileInputRef.current?.click(); }} style={{ width: '100%', padding: '9px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, fontFamily: 'Poppins, sans-serif', color: 'var(--text-primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                    <FiPaperclip size={13}/> Upload file
                  </button>
                  <button type="button" onClick={() => { setAttachMenuOpen(false); setShowAmsSelector(true); }} style={{ width: '100%', padding: '9px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, fontFamily: 'Poppins, sans-serif', color: 'var(--text-primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                    <FiFolder size={13}/> Select from AMS
                  </button>
                </div>)}
            </div>
            {messages.length > 0 && (<button className="cac-input-icon-btn" title="New conversation" onClick={handleNewChat}>
                <FiRefreshCw size={15}/>
              </button>)}

            
            <div className="cac-textarea-wrap">
              {mirrorSegments && (<div className="cac-textarea-mirror" aria-hidden="true">
                  {mirrorSegments}
                </div>)}
              <textarea ref={textareaRef} className={`cac-textarea${mirrorSegments ? ' cac-textarea--ghost' : ''}`} rows={1} placeholder="Ask anything — or @ to mention an insured, prospect, or policy…" value={input} disabled={streaming} onChange={e => { setInput(e.target.value); onValueChange(e.target.value); }} onKeyDown={handleKeyDown}/>
            </div>

            
            <div className="cac-input-right">
              {toolLog.length > 0 && (<button className="cac-input-icon-btn cac-input-icon-btn--log" title="Tool Call Log" onClick={() => window.dispatchEvent(new CustomEvent('showToolLogPanel', { detail: { entries: toolLog } }))}>
                  <FiList size={15}/>
                  <span className="cac-input-log-badge">{toolLog.length}</span>
                </button>)}
              <div className="cac-history-anchor" ref={historyRef}>
                <button className={`cac-input-icon-btn${showHistory ? ' cac-input-icon-btn--log-on' : ''}`} title="Session History" onClick={() => setShowHistory(v => !v)}>
                  <FiClock size={15}/>
                </button>
                {showHistory && (<SessionHistoryPanel onResume={handleResumeSession} onClose={() => setShowHistory(false)}/>)}
              </div>
              <div className="cac-input-divider"/>
              {streaming ? (
                <button className="cac-send-btn cac-send-btn--stop" onClick={handleInterrupt} title="Stop generating" aria-label="Stop generating">
                  <FiSquare size={13}/>
                </button>
              ) : (
                <button className={`cac-send-btn${canSend ? ' cac-send-btn--on' : ''}`} onClick={() => send()} disabled={!canSend} title="Send">
                  <FiSend size={14}/>
                </button>
              )}
            </div>
          </div>
          </div>
          <p className="cac-input-hint">Enter ↵ to send · Shift+Enter for newline · @ to mention an insured</p>
        </div>
      </main>

      
      <SelectFromAmsDialog visible={showAmsSelector} onClose={() => setShowAmsSelector(false)} onUpload={handleAmsFilesUpload}/>
    </div>);
};
export default ClaudeAgentChatPage;
